import React, { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { DataTable } from "../../components/table/data-table";
import { columns } from "../../components/table/columns";
import XanoAPI from "../../lib/XanoAPI";
import { ColumnDef } from "@tanstack/react-table";
import { getStates } from "../../lib/utils"; // ✅ Import state list
import { ClientType } from "../../types";
import { useAuth } from "../../lib/AuthContext";

export default function ClientsTable() {
  const api = XanoAPI.getInstance();
  const [searchParams, setSearchParams] = useSearchParams();
  const auth = useAuth();

  // ✅ Generate the State Name-to-Abbreviation Map dynamically
  const stateMap = useMemo(() => {
    const states = getStates();
    return states.reduce<{ [key: string]: string }>((map, state) => {
      if (state.value) {
        map[state.label] = state.value; // Full Name → Abbreviation
      }
      return map;
    }, {});
  }, []);

  // ✅ Extract filters from URL
  const page = Number(searchParams.get("page")) || 1;
  const perPage = Number(searchParams.get("perPage")) || 10;
  const search = searchParams.get("search") || "";
  const statusFilter = searchParams.get("status") || "";
  const lifecycleStage = searchParams.get("lifecycleStage") || "";
  const agencyFilter = searchParams.get("agency") || "";
  const stateFilterRaw = searchParams.get("state") || "";
  const userFilter = searchParams.get("userId") || ""; // ✅ New: Agent/User filter

  // ✅ Convert Full State Name to Abbreviation if necessary
  const stateFilter = stateMap[stateFilterRaw] || stateFilterRaw;

  // ✅ Check if current user is admin
  const isAdmin = auth?.currentUser?.user_permissions?.[0]?.role === "admin";

  // ✅ Update URL Params when filters change
  const updateParams = (key: string, value: string | number) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, String(value));
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams, { replace: true });
  };

  // ✅ Fetch Clients
  const fetchClients = async () => {
    console.log("🔍 Fetching Clients with Filters:", {
      page,
      search,
      perPage,
      statusFilter,
      lifecycleStage,
      agencyFilter,
      stateFilter,
      userFilter,
    });

    // 🔧 DEBUG: Log URL params
    console.log(
      "🔍 Current URL Search Params:",
      Object.fromEntries(searchParams.entries())
    );
    console.log("🔍 State Map:", stateMap);

    try {
      // ✅ Use main method with JSON filtering
      const response = await api.getAllClients(
        page,
        search,
        perPage,
        {
          status: statusFilter || undefined,
          lifecycleStage: lifecycleStage || undefined,
          agency: agencyFilter || undefined,
          state: stateFilter || undefined, // ✅ Use abbreviation
        },
        userFilter || undefined // ✅ Pass userId for agent filtering
      );

      console.log("✅ API RESPONSE:", response);
      return response;
    } catch (error: any) {
      console.error("❌ API ERROR:", error);

      // Check if it's the permission error from Xano
      if (
        error?.message?.includes("can_manage_clients") ||
        error?.message?.includes("Unable to locate var")
      ) {
        console.error(
          "⚠️ XANO PERMISSION ERROR: The backend endpoint has a permission check issue."
        );
        console.error(
          "💡 FIX NEEDED IN XANO: Update permission check from 'currentUser._permissions_of_user.can_manage_clients'"
        );
        console.error(
          "                        to 'currentUser.user_permissions[0].can_manage_clients'"
        );

        // Try again without userId filter as fallback
        if (userFilter) {
          console.log("🔄 Retrying without userId filter...");
          try {
            const fallbackResponse = await api.getAllClients(
              page,
              search,
              perPage,
              {
                status: statusFilter || undefined,
                lifecycleStage: lifecycleStage || undefined,
                agency: agencyFilter || undefined,
                state: stateFilter || undefined,
              },
              undefined // Don't pass userId
            );
            console.log("✅ Fallback succeeded (showing all clients)");
            return fallbackResponse;
          } catch (fallbackError) {
            console.error("❌ Fallback also failed:", fallbackError);
          }
        }
      }

      return { items: [] };
    }
  };

  // ✅ Fetch Agents (for admin user filter dropdown)
  const { data: agentsData } = useQuery({
    queryKey: ["agents"],
    queryFn: async () => {
      if (!isAdmin) return [];
      try {
        const response = await api.getUsersByAgencyId(
          auth?.currentUser?.agency_id || 0
        );
        console.log("✅ Fetched agency response:", response);

        // Extract members from the agency object
        // API returns array of agencies, we need the first one's members
        const members = response?.[0]?.members || [];
        console.log("✅ Extracted members:", members);

        return members;
      } catch (error) {
        console.error("❌ Error fetching agents:", error);
        return [];
      }
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    enabled: isAdmin, // Only fetch if user is admin
  });

  // ✅ React Query: Fetch Data
  const { data, isLoading, refetch } = useQuery({
    queryKey: [
      "clients",
      page,
      search,
      perPage,
      statusFilter,
      lifecycleStage,
      agencyFilter,
      stateFilter,
      userFilter,
    ],
    queryFn: fetchClients,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  // ✅ Refetch when filters change
  useEffect(() => {
    console.log("🔄 Refetching due to URL param changes...");
    refetch();
  }, [searchParams, refetch]);

  type ClientRow = {
    id: string;
    firstName: string;
    lastName: string;
    stateProvince: string;
    phoneNumber: string;
    type: string;
    status?: string;
    agentName?: string;
  };

  // ✅ Create agent lookup map
  const agentLookup = useMemo(() => {
    const lookup: Record<string, string> = {};
    (agentsData || []).forEach((agent: any) => {
      const firstName = agent.name?.split(' ')[0] || '';
      const lastName = agent.name?.split(' ').slice(1).join(' ') || '';
      const lastInitial = lastName ? `${lastName.charAt(0)}.` : '';
      lookup[String(agent.id)] = firstName && lastInitial ? `${firstName} ${lastInitial}` : agent.name || 'Unknown';
    });
    return lookup;
  }, [agentsData]);

  // ✅ Transform API Response
  const transformedData = useMemo(() => {
    console.log("📊 Transforming API Data:", data?.items);
    return (
      data?.items?.map((client: ClientType) => ({
        id: String(client.id),
        firstName: client.firstName ?? "",
        lastName: client.lastName ?? "",
        stateProvince: client.stateProvince ?? "",
        phoneNumber: client.phoneNumber ?? "",
        type: client.type ?? "federal",
        status: client.status ?? "Inactive",
        agentName: client.user_id ? agentLookup[String(client.user_id)] ?? "Unknown" : "Unassigned",
      })) || []
    );
  }, [data, agentLookup]);

  return (
    <DataTable<ClientRow, unknown>
      data={transformedData}
      columns={columns as ColumnDef<ClientRow, unknown>[]}
      page={page}
      setPage={(value) => updateParams("page", value)}
      perPage={perPage}
      setPerPage={(value) => updateParams("perPage", value)}
      totalPages={data?.pageTotal ?? 1}
      hasNextPage={!!data?.nextPage}
      hasPrevPage={!!data?.prevPage}
      search={search}
      setSearch={(value) => updateParams("search", value)}
      statusFilter={statusFilter}
      setStatusFilter={(value) => updateParams("status", value)}
      lifecycleStage={lifecycleStage}
      setLifecycleStage={(value) => updateParams("lifecycleStage", value)}
      agencyFilter={agencyFilter}
      setAgencyFilter={(value) => updateParams("agency", value)}
      stateFilter={stateFilter}
      setStateFilter={(value) => updateParams("state", value)}
      userFilter={userFilter}
      setUserFilter={(value) => updateParams("userId", value)}
      agents={agentsData || []}
      isAdmin={isAdmin}
      isLoading={isLoading}
    />
  );
}
